import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Warning } from "@phosphor-icons/react";
import logo from "@/assets/logo.png";

const ROLE_LABELS = {
  owner: "Owner", store_manager: "Store Manager", assistant_manager: "Assistant Manager",
  shift_lead: "Shift Lead", sales_associate: "Sales Associate", stock_associate: "Stock Associate",
  visual_merchandiser: "Visual Merchandiser", read_only: "Read Only",
};

export default function InviteSignup({ token }) {
  const [state, setState] = useState({ loading: true, valid: false, email: "", role: "", needsUsername: false, error: "" });
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/validate/${token}`)
      .then((r) => setState({ loading: false, valid: true, email: r.data.email || "", role: r.data.role, needsUsername: !!r.data.needs_username, error: "" }))
      .catch((e) => setState({ loading: false, valid: false, email: "", role: "", needsUsername: false, error: formatApiError(e.response?.data?.detail) || "This invite link is invalid." }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (state.needsUsername && !/^[a-z0-9._-]{3,20}$/.test(username.trim().toLowerCase())) {
      toast.error("Choose a username: 3–20 characters, using letters, numbers, and . _ - only"); return;
    }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (!agreed) { toast.error("Please agree to the Terms & Conditions and Privacy Policy"); return; }
    setBusy(true);
    try {
      const payload = { token, name, password };
      if (state.needsUsername) payload.username = username.trim().toLowerCase();
      const { data } = await api.post("/invites/accept", payload);
      localStorage.setItem("token", data.token);
      toast.success("Welcome aboard! Signing you in…");
      window.location.href = "/";
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not complete sign up");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-2xl border-2 border-black bg-card hard-shadow p-7">
        <div className="flex flex-col items-center text-center mb-6">
          <img src={logo} alt="Short Stack Hawai'i" className="h-14 w-auto mb-2" />
          <h1 className="font-display text-2xl font-bold leading-tight">Join the team</h1>
          <p className="text-sm text-black/50">You've been invited to Short Stack Hawai'i</p>
        </div>

        {state.loading ? (
          <p data-testid="invite-loading" className="text-sm text-black/50 py-8 text-center">Checking your invite…</p>
        ) : !state.valid ? (
          <div data-testid="invite-invalid" className="rounded-xl border-2 border-black bg-[#FEE2E2] p-4 flex items-start gap-2">
            <Warning size={20} weight="fill" className="shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm">Invite unavailable</p>
              <p className="text-sm text-black/60">{state.error}</p>
              <a href="/" className="inline-block mt-2 text-sm font-bold text-primary underline">Go to sign in</a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
            {state.needsUsername ? (
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-black/50">Choose a username</label>
                <input data-testid="invite-username" value={username} onChange={(e) => setUsername(e.target.value)} required placeholder="e.g. justin"
                  autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  className="w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm mt-1" />
                <p className="text-[11px] text-black/40 mt-1">You'll use this username to sign in (no email needed). Role: <span className="font-bold">{ROLE_LABELS[state.role] || state.role}</span></p>
              </div>
            ) : (
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-black/50">Email</label>
                <input data-testid="invite-email" value={state.email} disabled className="w-full h-11 px-3 rounded-lg border-2 border-black bg-[#F3F4F6] text-black/60 text-sm mt-1" />
                <p className="text-[11px] text-black/40 mt-1">Role: <span className="font-bold">{ROLE_LABELS[state.role] || state.role}</span></p>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-black/50">Your name</label>
              <input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe"
                className="w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-black/50">Password</label>
              <input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 6 characters"
                className="w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-black/50">Confirm password</label>
              <input data-testid="invite-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter password"
                className="w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm mt-1" />
            </div>
            <label data-testid="invite-agree" className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" data-testid="invite-agree-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0" />
              <span className="text-xs text-black/70 leading-relaxed">
                I have read and agree to the{" "}
                <a href="/terms.html" target="_blank" rel="noreferrer" className="underline font-bold">Terms &amp; Conditions</a>{" "}and{" "}
                <a href="/privacy.html" target="_blank" rel="noreferrer" className="underline font-bold">Privacy Policy</a>.
              </span>
            </label>
            <button data-testid="invite-submit" type="submit" disabled={busy || !agreed} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60 flex items-center justify-center gap-2">
              <PaperPlaneTilt size={18} weight="fill" /> {busy ? "Creating account…" : "Create my account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
